import type { CurrentStarterInfo } from '@racemap/utilities/api-client';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { labelFormatDistance, labelFormatVelocity } from '@racemap/utilities/formattings/units';
import type { PointWithOffsetTimeNumber } from '@racemap/utilities/functions/codec';
import { unitAwareVelocity } from '@racemap/utilities/functions/units';
import type { UnitType } from '@racemap/utilities/types/units';
import type { currentAPIColors } from '../consts';
import { formatDurationHHMMSS } from '../utils';
import type {
  DistanceGapSegment,
  DistanceGType,
  GapSegments,
  TimeGapSegment,
  TimeGType,
} from './gapSegments';
import { type AnchorMap, clamp, type GGeometry, type GType } from './labelSprings';
import { GC_INNER_H } from './layout';
import type { GapTimes, Scales } from './scales';

// Everything is in normalised plot coordinates: [0,1]×[0,1], origin bottom-left, y up.

type SVGDot = {
  time: number;
  offset: number;
  color: string;
  type: 'participant' | 'leader';
  isBig?: boolean; // the current position, drawn larger than the trail
  strokeColor?: string;
};

type SVGLine = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  width?: number;
  striped?: boolean;
};

type SVGText = {
  x: number;
  y: number;
  content: string;
  color: string;
  orientation: 'horizontal' | 'vertical';
  angle?: number;
};

type SVGRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  fillColor: string;
};

export type ChartElements = {
  dots: Array<SVGDot>;
  lines: Array<SVGLine>;
  texts: Array<SVGText>;
  rects: Array<SVGRect>;
};

export type ChartElementsInput = {
  scales: Scales;
  times: GapTimes;
  starterCurrent: CurrentStarterInfo | null;
  leaderCurrent: CurrentStarterInfo | null;
  segments: GapSegments;
  anchors: AnchorMap | null;
  geometry: GGeometry | null;
  rawStarterPoints: Array<PointWithOffsetTimeNumber>;
  rawLeaderPoints: Array<PointWithOffsetTimeNumber>;
  avgSpeedWindowSeconds: number;
  unitType: UnitType;
  colors: ReturnType<typeof currentAPIColors>;
  innerWidth: number;
};

export function buildChartElements(input: ChartElementsInput): ChartElements {
  const { scales, times, starterCurrent, leaderCurrent, segments, colors, unitType } = input;
  const { resultTime, gapCalcTime } = times;
  const out: ChartElements = { dots: [], lines: [], texts: [], rects: [] };
  const helper = (l: Omit<SVGLine, 'color'>) => out.lines.push({ ...l, color: colors.helperLine });

  // The label's animated position, or `fallback` until the springs have all eight anchors.
  const labelY = (type: GType, fallback: number) => {
    const anchor = input.anchors?.[type];
    const current = input.geometry?.[type];
    return anchor != null && current != null
      ? clamp(anchor[1] + current.deltaY, 0.03, 0.97)
      : fallback;
  };
  const labelX = (type: GType, x1: number, fallback: number) => {
    const anchor = input.anchors?.[type];
    const current = input.geometry?.[type];
    return anchor != null && current != null
      ? clamp(anchor[0] + current.deltaX, Math.max(0.05, x1 + 0.015), 0.97)
      : fallback;
  };

  // A time gap: a bar at the label height, dashed drop lines to the offset it is measured at.
  const timeGap = (
    type: TimeGType,
    { x1, x2, y1, value }: TimeGapSegment,
    fallbackY: number,
    withBaseline: boolean,
  ) => {
    const y = labelY(type, fallbackY);
    helper({ x1, y1, x2: x1, y2: y, striped: true });
    out.lines.push({ x1, y1: y, x2, y2: y, color: colors[type], width: 2 });
    if (withBaseline) helper({ x1, y1, x2, y2: y1, width: 1 });
    out.texts.push({
      x: 0.5 * (x1 + x2),
      y,
      content: `${type}: ${formatDurationHHMMSS(value)}`,
      color: colors[type],
      orientation: 'horizontal',
    });
    helper({ x1: x2, y1, x2, y2: y, striped: true });
  };

  // A distance gap: a vertical bar beside x1, dashed lines back to both of its ends.
  const distanceGap = (
    type: DistanceGType,
    { x1, y1, y2 }: DistanceGapSegment,
    fallbackX: number,
    label: string,
  ) => {
    const x = labelX(type, x1, fallbackX);
    helper({ x1, y1: y2, x2: x, y2, striped: true });
    out.lines.push({ x1: x, y1, x2: x, y2, color: colors[type], width: 2 });
    out.texts.push({
      x,
      y: (y1 + y2) / 2,
      content: `${type}: ${label}`,
      color: colors[type],
      orientation: 'vertical',
    });
    helper({ x1, y1, x2: x, y2: y1, striped: true });
  };

  // The current position and its speed line across the plot.
  const runner = (
    reading: CurrentStarterInfo['current'],
    fromStart: number,
    color: string,
    strokeColor: string,
    type: SVGDot['type'],
  ) => {
    const time = new Date(reading.time).getTime();
    out.dots.push({
      time: scales.toX(time),
      offset: scales.toY(fromStart),
      color,
      strokeColor,
      isBig: true,
      type,
    });
    if (reading.speedSt != null) {
      speedLine(time, fromStart, reading.speedSt, color);
    }
    return time;
  };

  const speedLine = (time: number, fromStart: number, speed: number, color: string) => {
    const x1 = scales.toX(scales.minTime);
    const y1 = scales.toY(fromStart - ((time - scales.minTime) / OneSecondInMillis) * speed);
    const x2 = scales.toX(scales.maxTime);
    const y2 = scales.toY(fromStart + ((scales.maxTime - time) / OneSecondInMillis) * speed);
    out.lines.push({ x1, y1, x2, y2, color });

    // Label at 90% of the part of the line inside the plot (x always spans [0,1]).
    const yDelta = y2 - y1;
    let tStart = 0;
    let tEnd = 1;
    if (yDelta !== 0) {
      if (y1 < 0) tStart = Math.max(tStart, -y1 / yDelta);
      if (y1 > 1) tStart = Math.max(tStart, (1 - y1) / yDelta);
      if (y2 > 1) tEnd = Math.min(tEnd, (1 - y1) / yDelta);
      if (y2 < 0) tEnd = Math.min(tEnd, -y1 / yDelta);
    }
    if (tStart >= tEnd) return;
    const tLabel = tStart + 0.9 * (tEnd - tStart);
    const slopeNorm = (speed * scales.timeRange) / OneSecondInMillis / scales.offsetRange;
    out.texts.push({
      x: x1 + tLabel * (x2 - x1),
      y: y1 + tLabel * yDelta,
      content: `${labelFormatVelocity(unitAwareVelocity(3.6 * speed, unitType), unitType, 1)}`,
      color,
      orientation: 'horizontal',
      angle: Math.atan2(-slopeNorm * GC_INNER_H, input.innerWidth) * (180 / Math.PI),
    });
  };

  const speedWindow = (time: number, fromStart: number, color: string) => {
    out.rects.push({
      x: scales.toX(time - input.avgSpeedWindowSeconds * OneSecondInMillis),
      y: 0,
      width: (input.avgSpeedWindowSeconds * OneSecondInMillis) / scales.timeRange,
      height: scales.toY(fromStart),
      fillColor: `${color}4d`,
    });
  };

  const trail = (points: Array<PointWithOffsetTimeNumber>, color: string, type: SVGDot['type']) => {
    for (const p of points) {
      if (p.time < scales.minTime) continue;
      out.dots.push({ time: scales.toX(p.time), offset: scales.toY(p.offset), color, type });
    }
  };

  const verticalAt = (time: number, striped?: boolean) => {
    out.lines.push({
      x1: scales.toX(time),
      y1: scales.toY(scales.minOffset),
      x2: scales.toX(time),
      y2: scales.toY(scales.maxOffset),
      color: '#aaa',
      striped,
    });
  };

  if (resultTime != null) verticalAt(resultTime, true);
  if (gapCalcTime != null) verticalAt(gapCalcTime);

  if (starterCurrent != null) {
    const s = starterCurrent.current;
    const { fromStart, fromStartEx } = s;
    if (fromStart != null) {
      const time = runner(
        s,
        fromStart,
        colors.participant,
        colors.participantStroke,
        'participant',
      );

      if (gapCalcTime != null) {
        speedWindow(time, fromStart, colors.participant);
        const { gapDistance, gap, gapChase, gapAverage } = segments;
        if (gapDistance != null) {
          distanceGap(
            'gapDistance',
            gapDistance,
            gapDistance.x1 + 0.08,
            labelFormatDistance(gapDistance.value, unitType, 1),
          );
        }
        if (gap != null) timeGap('gap', gap, gap.y1 - 0.05, true);
        if (gapChase != null) timeGap('gapChase', gapChase, gapChase.y1 + 0.08, true);
        if (gapAverage != null) timeGap('gapAverage', gapAverage, gapAverage.y1 + 0.05, true);
      }
    }

    if (resultTime != null && fromStartEx != null) {
      const { gapEx, gapDistanceEx, gapChaseEx, gapAverageEx } = segments;
      if (gapEx != null && fromStart) {
        const fallbackY = gapEx.y1 - (0.1 + scales.toY(fromStartEx) - scales.toY(fromStart));
        timeGap('gapEx', gapEx, fallbackY, false);
      }
      if (gapDistanceEx != null && gapCalcTime != null) {
        distanceGap(
          'gapDistanceEx',
          gapDistanceEx,
          gapDistanceEx.x1 + (0.1 + scales.toX(gapCalcTime) - scales.toX(resultTime)),
          labelFormatDistance(gapDistanceEx.value, unitType),
        );
      }
      if (gapChaseEx != null && leaderCurrent?.current.fromStart != null) {
        timeGap('gapChaseEx', gapChaseEx, gapChaseEx.y1 + 0.14, false);
      }
      if (gapAverageEx != null && fromStart != null) {
        const fallbackY =
          gapAverageEx.y1 - (-0.1 + scales.toY(fromStartEx) - scales.toY(fromStart));
        timeGap('gapAverageEx', gapAverageEx, fallbackY, false);
      }
    }
  }

  trail(input.rawStarterPoints, colors.participant, 'participant');

  if (leaderCurrent != null) {
    const l = leaderCurrent.current;
    if (l.fromStart != null) {
      const time = runner(l, l.fromStart, colors.leader, colors.leaderStroke, 'leader');
      speedWindow(time, l.fromStart, colors.leader);
    }
    trail(input.rawLeaderPoints, colors.leader, 'leader');
  }

  return out;
}
